import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiX } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Calendar from './Calendar';
import { formatDateRange, validateDateRange, isSameDay } from '../utils/dateHelpers';
import { useAuth } from '../contexts/AuthContext';
import { getWeekStartDay } from '../services/userPreferencesService';

function DateRangePicker({
  startDate,
  endDate,
  onChange,
  placeholder = 'Select date range',
  className = '',
  disabled = false,
  inline = false,
  minDate,
  maxDate,
}) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(startDate ? new Date(startDate) : new Date());
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  // 0 = Sunday, 1 = Monday. Defaults to Monday until the user preference loads,
  // matching the toggle preset logic in dateHelpers.computeRangeForMode.
  const [weekStartsOn, setWeekStartsOn] = useState(1);
  const autoCloseTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await getWeekStartDay(user);
        if (!cancelled) setWeekStartsOn(v);
      } catch {
        // Keep Monday default on lookup failure.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sync temp state when modal opens (modal-mode) or when external props change (inline-mode).
  useEffect(() => {
    if (inline) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      if (startDate) setCurrentMonth(new Date(startDate));
      return;
    }
    if (isOpen) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setValidationErrors([]);
      if (startDate) setCurrentMonth(new Date(startDate));
    }
  }, [isOpen, startDate, endDate, inline]);

  useEffect(() => () => {
    if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
  }, []);

  const commit = (start, end) => {
    if (!start || !end) return false;
    const validation = validateDateRange(start, end);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return false;
    }
    onChange(start, end);
    setValidationErrors([]);
    return true;
  };

  const handleDateSelect = (date) => {
    // No range yet, or already-complete range → start a new selection.
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      setTempStartDate(date);
      setTempEndDate(null);
      setValidationErrors([]);
      return;
    }

    // We have a start, no end yet.
    if (date < tempStartDate) {
      // Clicked before start: reset start.
      setTempStartDate(date);
      setTempEndDate(null);
      setValidationErrors([]);
      return;
    }

    if (isSameDay(date, tempStartDate)) {
      // Single-day range.
      setTempEndDate(date);
      finishSelection(tempStartDate, date);
      return;
    }

    setTempEndDate(date);
    finishSelection(tempStartDate, date);
  };

  const finishSelection = (start, end) => {
    if (inline) {
      // Inline: commit immediately, no modal to close.
      commit(start, end);
      return;
    }
    // Modal: commit + auto-close after a brief tick so the user sees the
    // final selection settle before the modal animates away.
    const ok = commit(start, end);
    if (!ok) return;
    if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    autoCloseTimer.current = setTimeout(() => {
      setIsOpen(false);
    }, 180);
  };

  const handleCancel = () => {
    if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsOpen(false);
    setValidationErrors([]);
    setHoveredDate(null);
  };

  const handleClear = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setValidationErrors([]);
    if (inline) onChange(null, null);
  };

  const displayText = startDate && endDate
    ? formatDateRange(startDate, endDate, isMobile)
    : placeholder;

  const nextMonth = (() => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() + 1);
    return d;
  })();

  const renderBody = (isModal) => (
    <div className={isModal ? 'p-3 sm:p-4 overflow-y-auto max-h-[calc(100vh-16rem)] md:max-h-[calc(80vh-120px)]' : ''}>
      <div className={`flex justify-center ${!isMobile ? 'md:gap-6' : ''}`}>
        <Calendar
          currentMonth={currentMonth}
          onMonthChange={setCurrentMonth}
          startDate={tempStartDate}
          endDate={tempEndDate}
          onDateSelect={handleDateSelect}
          minDate={minDate || new Date()}
          maxDate={maxDate || new Date(2030, 11, 31)}
          hoveredDate={hoveredDate}
          onDateHover={setHoveredDate}
          showPrevChevron={true}
          showNextChevron={isMobile}
          weekStartsOn={weekStartsOn}
        />
        {!isMobile && (
          <Calendar
            currentMonth={nextMonth}
            onMonthChange={(m) => {
              const d = new Date(m);
              d.setMonth(d.getMonth() - 1);
              setCurrentMonth(d);
            }}
            startDate={tempStartDate}
            endDate={tempEndDate}
            onDateSelect={handleDateSelect}
            minDate={minDate || new Date()}
            maxDate={maxDate || new Date(2030, 11, 31)}
            hoveredDate={hoveredDate}
            onDateHover={setHoveredDate}
            showPrevChevron={false}
            showNextChevron={true}
            weekStartsOn={weekStartsOn}
          />
        )}
      </div>

      {(tempStartDate || tempEndDate) && (
        <div className="mt-4 p-3 bg-accent/10 border border-accent rounded-lg">
          <div className="flex items-center justify-between text-sm flex-wrap gap-2">
            <div className="flex items-center space-x-4">
              <span className="text-text-secondary">
                <strong>From:</strong> {tempStartDate ? tempStartDate.toLocaleDateString('en-GB') : 'Not selected'}
              </span>
              <span className="text-text-secondary">
                <strong>To:</strong> {tempEndDate ? tempEndDate.toLocaleDateString('en-GB') : 'Not selected'}
              </span>
            </div>
            {tempStartDate && tempEndDate && (
              <span className="font-bold text-accent">
                {validateDateRange(tempStartDate, tempEndDate).dayCount} days
              </span>
            )}
            {(tempStartDate || tempEndDate) && (
              <button
                type="button"
                onClick={handleClear}
                className="text-xs text-text-muted hover:text-text-secondary underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {validationErrors.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 bg-red-50 border border-red-200/60 rounded-xl"
        >
          <div className="flex items-start">
            <SafeIcon icon={FiX} className="w-5 h-5 text-status-error mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-bold text-red-800 mb-1">Invalid Date Range</h4>
              <ul className="text-sm text-status-error space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );

  if (inline) {
    return (
      <div className={className}>
        {renderBody(false)}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <motion.div
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
        onClick={() => !disabled && setIsOpen(true)}
        className={`border border-border-subtle rounded-xl p-4 cursor-pointer transition-all duration-200 min-h-[44px] ${
          disabled ? 'bg-surface cursor-not-allowed' : 'bg-surface hover:border-accent'
        } ${isOpen ? 'border-accent' : ''}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <label className="block text-sm font-bold text-text-secondary mb-1">Date Range</label>
            <p className={`font-medium ${startDate && endDate ? 'text-text-primary' : 'text-text-muted'}`}>
              {displayText}
            </p>
            {startDate && endDate && (
              <p className="text-xs text-text-muted mt-1">
                {validateDateRange(startDate, endDate).dayCount} days
              </p>
            )}
          </div>
          <SafeIcon icon={FiCalendar} className="w-5 h-5 text-text-muted" />
        </div>
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${
              isMobile ? 'flex items-end pb-16' : 'flex items-center justify-center p-4'
            }`}
            onClick={handleCancel}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: isMobile ? 50 : 0 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: isMobile ? 50 : 0 }}
              className={`bg-card shadow-modal w-full overflow-hidden ${
                isMobile
                  ? 'rounded-t-xl max-h-[calc(100vh-4rem)]'
                  : 'rounded-xl max-w-3xl max-h-[85vh] mx-auto'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 sm:p-5 border-b border-border-subtle">
                <h2 className="text-lg sm:text-xl font-bold text-text-primary truncate">Select Date Range</h2>
                <button
                  onClick={handleCancel}
                  className="w-11 h-11 bg-surface rounded-xl flex items-center justify-center hover:bg-card transition-colors duration-200 ml-4 flex-shrink-0"
                  aria-label="Close date picker"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5 text-text-secondary" />
                </button>
              </div>

              {renderBody(true)}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default DateRangePicker;
